



























































































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  toRef,
} from '@nuxtjs/composition-api';
import LazyHydrate from 'vue-lazy-hydration';

import CompatibleSaves from '~/components/CompatibleSaves.vue';
import ProductDelivery from '~/components/Delivery.vue';
import SvgImage from '~/components/General/SvgImage.vue';
import ProductPrice from '~/components/Price.vue';
import PrintHoverText from '~/components/PrintHoverText.vue';
import ProductArrow from '~/components/Product/components/Arrow.vue';
import ProductBullets from '~/components/ProductBullets/PageType/PageType.vue';
import RatingStars from '~/components/RatingStars/RatingStars.vue';
import ProductStock from '~/components/Stock/Stock.vue';
// import TierPrices from '~/components/TierPrices/TierPrices.vue';
import Yields from '~/components/Yields/Yields.vue';
import {
  ProductInterface, useMagentoConfiguration, useProductBullets, useProductPageState, useStructuredData, useTax,
} from '~/composables';
import useProductPage from '~/composables/useProductPage';
import { PRODUCT_TABS } from '~/composables/useProductTabs';
import { AVAILABLE_PRODUCT_VISIBILITY, PRINTER_REFURBISHED, REFURBISHED_IN_SKU } from '~/constants';
import isServer from '~/helpers/isServer';
import {
  checkIsArrow,
  getBrandTitle,
  getCompatibilityBrand,
  isGenuine,
  isHP,
  isPrinter,
  isSwitchSave,
  removeDomain,
} from '~/helpers/productHelpers';
import { smoothScrollToElement } from '~/helpers/smoothScrollToElement';
import { useProductGallery } from '~/modules/catalog/product/composables/useProductGallery';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';

import ProductCrossSelling from '../CrossSelling.vue';
import Gallery from '../Gallery/Gallery.vue';
import ProductReviews from '../ProductReviews.vue';
import ProductTabs from '../ProductTabs/ProductTabs.vue';
import ProductTitle from '../Title/Title.vue';

export default defineComponent({
  name: 'SimpleProduct',
  components: {
    LazyHydrate,
    ProductTabs,
    Yields,
    RatingStars,
    ProductCrossSelling,
    ProductStock,
    ProductArrow,
    // TierPrices,
    ProductPrice,
    ProductDelivery,
    ProductBullets,
    Gallery,
    ProductTitle,
    ProductReviews,
    SvgImage,
    CompatibleSaves,
    PrintHoverText,
    SwitchSaveContent: () => import('~/components/switch-save/components/Content/Content.vue'),
    BuyTogetherProduct: () => import('~/components/Product/components/BuyTogether/BuyTogether.vue'),
    PrinterBullets: () => import('~/components/ProductBullets/PrinterType/PrinterType.vue'),
    ProductsInRange: () => import('../ProductsInRange/ProductsInRange.vue'),
  },
  props: {
    product: {
      type: [Object, null] as PropType<ProductInterface>,
      default: null,
    },
    isFetching: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const product = toRef(props, 'product');
    const { productGallery } = useProductGallery(product);
    const { setBrand, setCompatibilityBrand, setActiveTab } = useProductPageState();
    const { collectProductJsonLd } = useStructuredData();
    const { reviews } = useProductPage();
    const { getCalculatedPrice } = useTax();
    const { magentoBaseUrl } = useMagentoConfiguration();

    const currentRating = computed(() => (!product.value.rating_summary ? 0 : +((product.value.rating_summary * 5) / 100).toFixed(1)));
    const reviewsTotal = computed(() => product.value.review_count);
    const compatibilityBrand = computed(() => getCompatibilityBrand(product.value.compatibility_list, magentoBaseUrl.value));
    const brandTitle = computed(() => getBrandTitle(product.value.compatibility_list, magentoBaseUrl.value));
    const showDeliveryMessage = computed(() => product.value.stock?.is_in_stock && !product.value.new_to_market && product.value.stock?.qty > 0);
    const isArrow = computed(() => checkIsArrow(product.value));
    const isCrossSelling = computed(() => {
      const altProduct = product.value.alternative_product;
      const isAltProduct = altProduct?.status !== 2 && AVAILABLE_PRODUCT_VISIBILITY.includes(altProduct?.visibility);

      if (!isAltProduct || product.value.visibility === 5) {
        return false;
      }

      return !altProduct?.new_to_market && altProduct.stock?.is_in_stock;
    });
    const { showCompatibilityList } = useProductBullets({ product: props.product });
    const isBuyTogetherProduct = computed(() => product.value.stock?.is_in_stock && product.value.buy_together?.stock?.is_in_stock);
    const showSwitchSave = computed(() => isSwitchSave(product.value));
    const jsonLd = computed(() => collectProductJsonLd(product.value, reviews.value || []));
    const isPrinterProduct = computed<boolean>(() => isPrinter(product.value));
    const isProductGenuine = computed(() => isGenuine(product.value));
    const isExclusiveLayout = computed(() => isHP(product.value) && isGenuine(product.value));
    const productsInRange = computed(() => product.value.products_in_range?.slice(0, 12) ?? []);

    const relatedPrinter = computed(() => product.value.related_printer);
    const cartridgesLink = computed(() => {
      const link = product.value.category_link?.category_link;

      if (!link) {
        return null;
      }

      return removeDomain(link, magentoBaseUrl.value);
    });
    // eslint-disable-next-line max-len
    const isRefurbished = computed(() => +(relatedPrinter.value?.printer_condition ?? 0) === PRINTER_REFURBISHED || relatedPrinter.value?.sku.includes(REFURBISHED_IN_SKU));
    // eslint-disable-next-line max-len
    const categoryNameForGtm = computed(() => {
      const breadcrumbs = getBreadcrumbs(product.value, Array.isArray(product.value.categories) ? [...product.value.categories].pop() : null);

      return breadcrumbs?.[1]?.text ? `${breadcrumbs[1].text || ''} ${breadcrumbs[0].text || ''}` : '';
    });
    const hasCompatibleSaves = computed(() => !isProductGenuine.value && product.value.alternative_product?.stock?.is_in_stock);
    const hasCompatibilityList = product?.value.compatibility_list && product?.value.compatibility_list?.length > 0;

    const showReviews = () => {
      setActiveTab(PRODUCT_TABS.reviews);
      smoothScrollToElement(`#${PRODUCT_TABS.reviews}`);
    };

    setBrand(brandTitle.value);
    setCompatibilityBrand(compatibilityBrand.value);

    return {
      productGallery,
      compatibilityBrand,
      currentRating,
      showDeliveryMessage,
      isArrow,
      isCrossSelling,
      showSwitchSave,
      jsonLd,
      productsInRange,
      isBuyTogetherProduct,
      isPrinterProduct,
      isRefurbished,
      relatedPrinter,
      cartridgesLink,
      isProductGenuine,
      isExclusiveLayout,
      reviewsTotal,
      categoryNameForGtm,
      hasCompatibleSaves,
      brandTitle,
      showReviews,
      getCalculatedPrice,
      showCompatibilityList,
      hasCompatibilityList,
    };
  },
  methods: {
    isServer() {
      return isServer;
    },
  },
});
